import * as React from 'react';
import { createPortal } from 'react-dom';
import { Check, Megaphone, Image as PostIcon } from 'lucide-react';
import { Button } from './ui/button.js';
import { cn } from '../lib/utils.js';

// Which of the two marketing flows to start.
//
// A picker rather than two buttons on the card: the difference between an
// organic post and a paid campaign is a billing decision, and the subtitle
// is where that gets said before anyone commits to a flow.

export type CreateKind = 'post' | 'campaign';

const OPTIONS: {
  kind: CreateKind;
  label: string;
  description: string;
  Icon: typeof PostIcon;
}[] = [
  {
    kind: 'post',
    label: 'Post',
    description:
      'Publish a listing to your connected pages. Free, goes out once.',
    Icon: PostIcon,
  },
  {
    kind: 'campaign',
    label: 'Campaign',
    description:
      'Run it as a paid ad with a budget, audience and schedule.',
    Icon: Megaphone,
  },
];

export function CreatePostOrCampaignModal({
  open,
  onClose,
  onContinue,
}: {
  open: boolean;
  onClose: () => void;
  onContinue: (kind: CreateKind) => void;
}) {
  const [selected, setSelected] = React.useState<CreateKind>('post');
  const [shown, setShown] = React.useState(false);
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      // Reset on each open so the picker never reflects a stale choice
      // from a run the user abandoned.
      setSelected('post');
      setShown(true);
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const r1 = requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true)),
      );
      return () => {
        cancelAnimationFrame(r1);
        document.body.style.overflow = prev;
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!shown) return null;

  // Portalled to <body>: `position: fixed` resolves against the nearest
  // transformed ancestor, not the viewport, so a modal rendered inside a
  // card can end up anchored to that card instead of the screen.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Create post or campaign"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative w-full max-w-xl overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="border-b border-[hsl(var(--hairline))] px-5 py-4">
          <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
            What are you creating?
          </h2>
          <p className="mt-2 font-sans text-[13px] leading-snug text-muted-foreground">
            Both promote a listing across your connected channels — only one
            of them spends money.
          </p>
        </div>

        <div
          role="radiogroup"
          aria-label="Creation type"
          className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2"
        >
          {OPTIONS.map(({ kind, label, description, Icon }) => {
            const active = selected === kind;
            return (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setSelected(kind)}
                // Double-click commits, so a decisive user doesn't have to
                // travel to the footer.
                onDoubleClick={() => onContinue(kind)}
                className={cn(
                  'group relative flex flex-col items-start gap-3 rounded-xl border p-4 text-left transition-colors',
                  active
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/25'
                    : 'border-border bg-card hover:border-primary/40 hover:bg-muted dark:border-white/10',
                )}
              >
                <div
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors',
                    active
                      ? 'bg-primary/15 text-primary'
                      : 'bg-muted text-foreground group-hover:bg-primary/10 group-hover:text-primary',
                  )}
                >
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-sans text-[14px] font-semibold leading-tight">
                    {label}
                  </div>
                  <p className="mt-1 font-sans text-[12px] leading-snug text-muted-foreground">
                    {description}
                  </p>
                </div>
                {/* A tick as well as the border — colour alone is not a
                    selection cue everyone can read. */}
                {active && (
                  <span
                    aria-hidden
                    className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground"
                  >
                    <Check className="h-3 w-3" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--hairline))] px-5 py-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={() => onContinue(selected)}>
            Continue
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
